import { Grid, GridItem } from "@innovationnorway/lighthouse-components";
import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { AccountTypeEnum } from "../../../main/models/account-type-enum";
import { BREAKPOINT_ONETAB, BREAKPOINT_TWOTABS } from "../../../utils/constants";
import useWindowSize from "../../../utils/windowsSize";
import { getUserAccounts } from "../../apis/getUserAccounts";
import { useLayout } from "../../hooks/useLayout";
import Tabs from "../Tabs/Tabs";
import AddAccountBySearch from "../addAccountBySearch/addAccountBySearch";
import { InfoBanner } from "../infoBanner/infoBanner";

interface ActorTabsProps {
  setCurrentAccountByAccountPublicId: (accountPublicId: string) => void;
  currentAccountPublicId?: string;
}

export interface ITabs {
  label: string;
  selected: boolean;
  favorite?: boolean;
  accountPublicId: string;
  accountId: string;
  accountType: AccountTypeEnum;
}

const ActorTabs = ({ setCurrentAccountByAccountPublicId, currentAccountPublicId }: ActorTabsProps) => {
  const { data, error, isValidating, mutate } = useSWR("userAccounts", getUserAccounts);
  const { translations } = useLayout();
  const [tabs, setTabs] = useState<ITabs[]>([]);
  const [tabsToBeShown, setTabsToBeShown] = useState<ITabs[]>([]);

  const windowSize = useWindowSize();
  const showTwoTabs = windowSize.width < BREAKPOINT_TWOTABS;
  const showOneTab = windowSize.width < BREAKPOINT_ONETAB;

  const reValidateTabs = () => {
    mutate();
  };

  useEffect(() => {
    const accountTabs = tabsToBeShown.map((tab) => {
      tab.selected = tab.accountPublicId === currentAccountPublicId;
      return tab;
    });
    const selectedTab = tabs.find((tab) => tab.accountPublicId === currentAccountPublicId);
    if (
      selectedTab != undefined &&
      tabsToBeShown.find((tab) => tab.accountPublicId == selectedTab?.accountPublicId) === undefined
    ) {
      selectedTab.favorite = true;
      accountTabs.unshift(selectedTab);
      accountTabs.pop();
    }
    setTabsToBeShown(accountTabs);
  }, [currentAccountPublicId]);

  useEffect(() => {
    if (data) {
      const newTabs = data.map((acc, index) => {
        return {
          label: acc.account.name,
          selected: acc.account.publicId === currentAccountPublicId,
          favorite: index < (showTwoTabs ? (showOneTab ? 1 : 2) : 3),
          accountPublicId: acc.account.publicId,
          accountId: acc.account.accountId,
          accountType: acc.account.accountType
        };
      });
      const otherTabs = newTabs.slice(0, showTwoTabs ? (showOneTab ? 1 : 2) : 3);
      setTabs(newTabs);
      setTabsToBeShown(otherTabs);
    }
  }, [data, showOneTab, showTwoTabs]);

  return (
    <>
      {tabsToBeShown.length > 0 && !isValidating && !error ? (
        <Grid>
          <GridItem spanLarge={10} spanMedium={9} spanSmall={4}>
            <Tabs
              tabs={tabsToBeShown}
              showHiddenTabs={tabs.length > tabsToBeShown.length}
              hiddenTabs={tabs}
              isExpanded
              setTabs={setTabsToBeShown}
              setHiddenTabs={setTabs}
              accounts={data}
              reValidateTabs={reValidateTabs}
              setCurrentAccountPublicId={setCurrentAccountByAccountPublicId}
            />
          </GridItem>
          <GridItem spanLarge={2} spanMedium={3} spanSmall={1} hideOnSmall>
            <AddAccountBySearch />
          </GridItem>
        </Grid>
      ) : (
        error &&
        !isValidating && (
          <Grid>
            <GridItem spanLarge={12} spanMedium={12} spanSmall={4}>
              {error.code === 404 ? (
                <InfoBanner variant="info">{translations.notFound}</InfoBanner>
              ) : (
                <InfoBanner variant="error">{error.message || translations.loadingFailed}</InfoBanner>
              )}
            </GridItem>
          </Grid>
        )
      )}
    </>
  );
};

export default ActorTabs;
